import { Overlay, ScrollArea, Space } from "@mantine/core";
import { useDebouncedCallback, useIntersection } from "@mantine/hooks";
import { useEffect, useMemo, useRef } from "react";
import { getSessionByTaskId, useActiveChatMessages, useAgentSdk, useChatStore } from "@/sdk";
import type { ChatTask } from "@/sdk/agent/chat";
import { CustomerTypingIndicator } from "./CustomerTypingIndicator";
import { MessageListItem } from "./MessageListItem";
import classes from "./message-component-styles.module.css";
import { NewMessagesScrollButton } from "./NewMessagesScrollButton";
import { renderSeparator } from "./renderSeparator";
import { type ScrollAreaViewport, scrollToBottom } from "./scrollUtils";
import { useMessageListItems } from "./useMessageListItems";

const SCROLLBAR_WIDTH = "1rem";
const TOP_SPACING = "xl";
const BOTTOM_SPACING = "md";

const topGradient = `linear-gradient(to bottom, var(--exwo-color-app-bg) 0, transparent var(--mantine-font-size-${TOP_SPACING}))`;
const bottomGradient = `linear-gradient(to top, var(--exwo-color-app-bg) 0, transparent var(--mantine-font-size-${BOTTOM_SPACING}))`;

const ScrollAreaFadeOverlay = ({ visible }: { visible: boolean }) => (
    <Overlay
        opacity={visible ? 1 : 0}
        mr={SCROLLBAR_WIDTH}
        id="scroll-area-overlay-fade-effect"
        aria-hidden
        gradient={`${bottomGradient}, ${topGradient}`}
        style={{ pointerEvents: "none" }}
    />
);

interface MessageListProps {
    chatTask: ChatTask;
    viewportRef: ScrollAreaViewport;
}

export default function MessageList({ chatTask, viewportRef }: MessageListProps) {
    const agentSdk = useAgentSdk();
    const messages = useActiveChatMessages(chatTask);
    const chatId = chatTask.chatId;
    const customerName = chatTask.customerName.value;

    const isCustomerTyping = useChatStore((state) => state.isCustomerTyping(chatId));

    const isScrollVisibleVar = !viewportRef.current
        ? false
        : viewportRef.current.scrollHeight !== viewportRef.current.clientHeight;
    const disableAutoscroll = useRef(false);
    const lastScrollPosition = useRef(0);

    const { ref: lastItemRef, entry } = useIntersection({
        root: viewportRef.current,
        threshold: 0.5, // Mostly visible
    });

    const debouncedSetAllMessagesRead = useDebouncedCallback((chatTask: ChatTask) => {
        void agentSdk.advanceReadStatus(chatTask, { allMessagesRead: true });
    }, 500);
    const handleScrollPositionChange = (newPosition: { x: number; y: number }) => {
        if (lastScrollPosition.current > newPosition.y && !entry?.isIntersecting) {
            // User scrolled up out of view of last message
            disableAutoscroll.current = true;
        } else {
            disableAutoscroll.current = false;
        }

        lastScrollPosition.current = newPosition.y;
        if (entry?.isIntersecting && chatTask.unreadMessageCount > 0) {
            // If the user is at the bottom of the scroll area and there are unread messages, mark all messages as read
            debouncedSetAllMessagesRead(chatTask);
        }
    };

    /** Memoized session ID - component is keyed by chatTask.id so this only calculates once per task */
    const sessionId = useMemo(() => {
        const session = getSessionByTaskId(chatTask.id);
        if (!session) {
            throw new Error(`Session not found for task ID: ${chatTask.id} in message list`); // should be an impossible case
        }
        return session.id;
    }, [chatTask.id]);

    // Get all the items for the message list, including messages, typing indicator, and separator

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        // Reset the disable autoscroll flag when the chat enters wrapping status
        if (chatTask.status === "wrapping") {
            disableAutoscroll.current = false;
        } //TODO: not sure that we care about this case, but leaving it here for now

        if (
            (isCustomerTyping && entry?.isIntersecting) ||
            !disableAutoscroll.current ||
            chatTask.status === "wrapping"
        ) {
            // If the customer is typing and the last message is visible, scroll to bottom
            scrollToBottom(viewportRef);
            debouncedSetAllMessagesRead(chatTask);
        }
    }, [messages.length, isCustomerTyping, chatTask.status]);

    // Run once when the component mounts to mark all messages as read
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        scrollToBottom(viewportRef);
        debouncedSetAllMessagesRead(chatTask);
    }, []);

    const messageListItems = useMessageListItems(messages, chatTask, isCustomerTyping, sessionId, customerName);

    return (
        <ScrollArea
            id="chat-message-list"
            aria-label="chat messages"
            pt="1"
            pb="1"
            pr="xs"
            pl="xs"
            scrollbarSize={SCROLLBAR_WIDTH}
            type="auto"
            offsetScrollbars="present"
            classNames={{ scrollbar: classes.scrollbar }}
            onScrollPositionChange={handleScrollPositionChange}
            viewportRef={viewportRef}
        >
            <Space h={TOP_SPACING} />
            {messageListItems.map((renderListItem, renderIdx) => {
                const isLastItem = renderIdx === messageListItems.length - 1;
                switch (renderListItem.type) {
                    case "message": {
                        const message = renderListItem.data;
                        const messageIndex = renderListItem.index;
                        const isLastMessage = messageIndex === messages.length - 1;

                        //TODO: break message author groups up for long times between messages
                        //TODO: refactor grouping logic to break when a separator is needed
                        //TODO: break grouping logic out into utility or build into preprocessing of messages
                        const firstOfAuthorGroup =
                            messageIndex === 0 || messages[messageIndex - 1].senderId !== message.senderId;
                        const lastOfAuthorGroup =
                            (isLastMessage && !(isCustomerTyping && message.senderType === "customer")) ||
                            (!isLastMessage && messages[messageIndex + 1].senderId !== message.senderId);

                        let lastMessageInGroupTime = message.dateCreated;
                        if (firstOfAuthorGroup && !lastOfAuthorGroup) {
                            // Look ahead to find the last message in this author group, loop starts at the next message and ends when the senderId changes
                            for (let i = messageIndex + 1; i < messages.length; i++) {
                                if (messages[i].senderId !== message.senderId) break;
                                lastMessageInGroupTime = messages[i].dateCreated;
                            }
                        }

                        return (
                            <MessageListItem
                                key={`msg-list-item-${message.messageType}-${message.index}`} //TODO: consider implementations with no index to use
                                message={message}
                                firstOfAuthorGroup={firstOfAuthorGroup}
                                lastOfAuthorGroup={lastOfAuthorGroup}
                                lastMessageInGroupTime={firstOfAuthorGroup ? lastMessageInGroupTime : null}
                                sessionId={sessionId}
                                customerName={customerName ?? "Customer"}
                                ref={isLastMessage ? lastItemRef : undefined} // Attach ref to the last message
                            />
                        );
                    }
                    case "typing":
                        return (
                            <CustomerTypingIndicator
                                key={`message-list-item-${messages.length}`} //TODO: change key to be more unique if needed
                                sessionId={renderListItem.data.sessionId}
                                customerName={renderListItem.data.customerName}
                                isLastCustomerMessage={renderListItem.data.isLastCustomerMessage}
                                ref={isLastItem ? lastItemRef : undefined}
                            />
                        );
                    case "separator":
                        return renderSeparator(
                            renderListItem.data.separatorType,
                            renderListItem.data.chatTask,
                            isLastItem ? lastItemRef : null,
                            renderIdx,
                        );
                }
            })}
            <NewMessagesScrollButton
                scrolledToBottom={!!entry?.isIntersecting}
                unreadCount={chatTask.unreadMessageCount}
                viewport={viewportRef}
            />
            <ScrollAreaFadeOverlay visible={isScrollVisibleVar} />
        </ScrollArea>
    );
}
